import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { AlertTriangle, Loader2, MapPin, Mic2, Music2 } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footerbar from '../components/Footerbar';

const API_BASE_URL = 'https://apem.riderup.uk/api';

export default function Shows() {
    const [shows, setShows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState('');
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        let active = true;

        const loadShows = async (coords = null) => {
            try {
                const params = coords ? { latitude: coords.latitude, longitude: coords.longitude } : {};
                const response = await axios.get(`${API_BASE_URL}/artist-shows/nearby`, { params });
                if (!active) return;
                setShows(response.data.shows || []);
            } catch (error) {
                console.error('Erro ao buscar shows:', error);
                if (active) setStatus('Nao foi possivel carregar os shows agora.');
            } finally {
                if (active) setLoading(false);
            }
        };

        if (!navigator.geolocation) {
            setStatus('GPS indisponível. Não é possível conectar a um show.');
            setLoading(false);
            return () => {
                active = false;
            };
        }

        navigator.geolocation.getCurrentPosition(
            (position) => {
                loadShows({
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                });
            },
            () => {
                if (active) { setStatus('Permita a localização para encontrar shows em até 100 metros.'); setLoading(false); }
            },
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
        );

        return () => {
            active = false;
        };
    }, []);

    return (
        <div style={styles.page}>
            <Navbar title="Shows ao Vivo" showLogo={false} />
            <main style={styles.content}>
                <section style={styles.header}>
                    <div style={styles.headerIcon}>
                        <Mic2 size={28} />
                    </div>
                    <div>
                        <h1 style={styles.title}>Cantores perto de voce</h1>
                        <p style={styles.subtitle}>Entre no show, mande um alo e acompanhe o repertorio.</p>
                    </div>
                </section>

                {(status || location.state?.showLocationNotice) && (
                    <div style={styles.notice}>
                        <AlertTriangle size={18} />
                        <span>{location.state?.showLocationNotice || status}</span>
                    </div>
                )}

                {loading ? (
                    <div style={styles.center}>
                        <Loader2 size={34} style={styles.spinner} />
                        <p>Buscando shows...</p>
                    </div>
                ) : shows.length === 0 ? (
                    <div style={styles.empty}>
                        <Mic2 size={42} />
                        <p>Nenhum show disponível em até 100 metros.</p>
                    </div>
                ) : (
                    <div style={styles.grid}>
                        {shows.map((show) => {
                            const profile = show.artist_profile || {};
                            const singer = profile.stage_name || profile.user?.name || 'Cantor';
                            return (
                                <button key={show.id} style={styles.card} onClick={() => navigate(`/shows/${show.id}`)}>
                                    <div style={styles.cardTop}>
                                        {profile.photo_url ? <img src={profile.photo_url} alt={singer} style={{...styles.avatar, objectFit: 'cover'}} /> : <div style={styles.avatar}>{singer.charAt(0).toUpperCase()}</div>}
                                        <span style={styles.liveBadge}>Ao vivo</span>
                                    </div>
                                    <h2 style={styles.showTitle}>{show.title}</h2>
                                    <p style={styles.singer}>{singer}</p>
                                    {show.distance !== undefined && show.distance !== null && (
                                        <p style={styles.meta}>
                                            <MapPin size={14} />
                                            {Math.round(Number(show.distance) * 1000)} m
                                        </p>
                                    )}
                                    <p style={styles.meta}>
                                        <Music2 size={14} />
                                        {(show.setlist_items || []).length} musicas no repertorio
                                    </p>
                                </button>
                            );
                        })}
                    </div>
                )}
            </main>
            <Footerbar />
        </div>
    );
}

const styles = {
    page: {
        minHeight: '100vh',
        backgroundColor: '#12121f',
        color: '#f0f0f0',
    },
    content: {
        padding: '78px 14px 86px',
        boxSizing: 'border-box',
    },
    header: {
        display: 'flex',
        gap: '14px',
        alignItems: 'center',
        marginBottom: '18px',
    },
    headerIcon: {
        width: '54px',
        height: '54px',
        borderRadius: '14px',
        backgroundColor: '#2a2a4e',
        color: '#a0a0ff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    title: {
        margin: 0,
        fontSize: '24px',
        lineHeight: 1.1,
    },
    subtitle: {
        margin: '6px 0 0',
        color: '#b8b8d8',
        fontSize: '14px',
    },
    notice: {
        display: 'flex',
        gap: '8px',
        alignItems: 'center',
        color: '#ffe08a',
        backgroundColor: 'rgba(255, 224, 138, 0.09)',
        border: '1px solid rgba(255, 224, 138, 0.22)',
        borderRadius: '12px',
        padding: '10px 12px',
        marginBottom: '16px',
        fontSize: '13px',
    },
    center: {
        minHeight: '280px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#b8b8d8',
    },
    spinner: {
        animation: 'spin 1s linear infinite',
        color: '#a0a0ff',
    },
    empty: {
        minHeight: '280px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px',
        textAlign: 'center',
        color: '#b8b8d8',
    },
    grid: {
        display: 'grid',
        gap: '14px',
    },
    card: {
        width: '100%',
        border: '1px solid #2f2f55',
        borderRadius: '14px',
        backgroundColor: '#1a1a2e',
        color: '#f0f0f0',
        textAlign: 'left',
        padding: '16px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.22)',
    },
    cardTop: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '12px',
    },
    avatar: {
        width: '44px',
        height: '44px',
        borderRadius: '12px',
        backgroundColor: '#6a0dad',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: '700',
    },
    liveBadge: {
        borderRadius: '999px',
        backgroundColor: '#16a34a',
        color: '#fff',
        padding: '5px 10px',
        fontSize: '12px',
        fontWeight: 700,
    },
    showTitle: {
        margin: '0 0 5px',
        fontSize: '18px',
        lineHeight: 1.25,
    },
    singer: {
        margin: '0 0 12px',
        color: '#d7d7ff',
        fontWeight: 600,
    },
    meta: {
        display: 'flex',
        gap: '6px',
        alignItems: 'center',
        margin: '6px 0 0',
        color: '#b8b8d8',
        fontSize: '13px',
    },
};
